import { CmDetailsDataStructure, DetailsDataStructure } from 'components/DetailsPageBoilerplate/types';
import { Option } from 'shared/interface/globalInterface';
import { ResponseData } from 'store/details/types';
import { mapOldAddress } from 'utils/address';
import { AdminDataMapper, AdminDetailsDataMapper } from 'utils/api/admins/types';
import {
  AdminDetailsResponseData,
  ClubAdminDetailsClubResponseData,
  ClubDetailsResponseData,
  FirmAdminDetailsFirmResponseData,
  FirmDetailsResponseData,
  NetworkAdminDetailsNetworkResponseData,
  NetworkDetailsResponseData,
} from 'utils/api/types';
import { format } from 'utils/date';
import { Nullable } from 'utils/types';

const DATE_FORMAT = 'DD/MM/YYYY hh:mm a';

const getClubName = (id: Nullable<string>, clubs: Option[]): string => {
  if (!id) return '';
  const club = clubs.find((club) => club.id === id);
  return club && club.name ? club.name : '';
};

export const firmDataMapper = {
  map: (response: ResponseData<FirmDetailsResponseData>): CmDetailsDataStructure => {
    const { data: responseData, resources } = response;

    return {
      admins: [],
      company: {
        id: responseData.id.toString(),
        phone: responseData.phone,
        lmcStatus: responseData.is_active,
        fcaStatus: responseData.fca.is_active,
        dueDiligenceStatus: responseData.due_diligence.dd_status,
        dueDiligence: responseData.due_diligence,
        dueDiligenceChecklist: responseData.due_diligence_checklist,
        address: mapOldAddress(responseData.address),
        name: responseData.name,
        email: responseData.email,
        fcaNumber: responseData.fca ? responseData.fca.number : undefined,
        network: responseData.network ? responseData.network.name : null,
        submissionRoute: {
          value: responseData.submission_route.toString(),
          option:
            responseData.submission_route === 0
              ? 'DIRECT'
              : getClubName(responseData.club ? responseData.club.id : null, resources.clubs),
        },
        hasClub: responseData.has_club_submission_route,
        registeredAt: responseData.registration_datetime,
        commissionTerms: responseData.commission_terms || [],
        firmType: responseData.type,
        updatedAt: format(responseData.updated_at).dateTime(DATE_FORMAT),
        termsOfBusiness: responseData.terms_of_business,
        isPackager: responseData.is_packager,
      },
      resources: resources,
    };
  },
} as AdminDataMapper;

export const clubDataMapper = {
  map: (response: ResponseData<ClubDetailsResponseData>): CmDetailsDataStructure => {
    const { data: responseData, resources } = response;

    return {
      admins: responseData.admins.map((admin) => ({
        address: mapOldAddress(admin.address),
        email: admin.email,
        id: admin.id.toString(),
        fcaStatus: admin.fca ? admin.fca.is_active : undefined,
        lmcStatus: admin.is_active,
        firstName: admin.first_name,
        lastName: admin.last_name,
        phone: admin.phone,
        fcaNumber: admin.fca ? admin.fca.number : undefined,
      })),
      company: {
        id: responseData.id.toString(),
        phone: responseData.phone,
        lmcStatus: responseData.is_active,
        fcaStatus: undefined,
        dueDiligenceStatus: responseData.due_diligence.dd_status,
        dueDiligence: responseData.due_diligence,
        address: mapOldAddress(responseData.address),
        name: responseData.name,
        email: responseData.email,
        fcaNumber: undefined,
        registeredAt: responseData.registered_at,
        commissionTerms: responseData.commission_terms || [],
        updatedAt: format(responseData.updated_at).dateTime(DATE_FORMAT),
      },
      resources: resources,
    };
  },
} as AdminDataMapper;

export const networkDataMapper = {
  map: (response: ResponseData<NetworkDetailsResponseData>): CmDetailsDataStructure => {
    const { data: responseData, resources } = response;

    return {
      admins: responseData.admins.map((admin) => ({
        address: mapOldAddress(admin.address),
        email: admin.email,
        id: admin.id.toString(),
        fcaStatus: admin.fca ? admin.fca.is_active : undefined,
        lmcStatus: admin.is_active,
        isLMCActive: undefined,
        firstName: admin.first_name,
        lastName: admin.last_name,
        phone: admin.phone,
        fcaNumber: admin.fca ? admin.fca.number : undefined,
      })),
      company: {
        id: responseData.id.toString(),
        phone: responseData.phone_number,
        lmcStatus: responseData.is_active,
        fcaStatus: responseData.fca.is_active,
        dueDiligenceStatus: responseData.due_diligence.dd_status,
        dueDiligence: responseData.due_diligence,
        address: mapOldAddress(responseData.address),
        name: responseData.name,
        email: responseData.email,
        fcaNumber: responseData.fca ? responseData.fca.number : undefined,
        registeredAt: '',
        commissionTerms: responseData.commission_terms || [],
        hasClub: responseData.has_club_submission_route,
        submissionRoute: responseData.submission_route,
        updatedAt: format(responseData.updated_at).dateTime(DATE_FORMAT),
      },
      resources: resources,
    };
  },
} as AdminDataMapper;

export default {
  map: (): DetailsDataStructure => {
    return {
      admins: [],
      company: {
        address: {
          houseNumber: '',
          county: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          postcode: '',
        },
        id: '',
        fcaNumber: '',
        email: '',
        name: '',
        phone: '',
        fcaStatus: undefined,
        lmcStatus: undefined,
        registeredAt: '',
      },
      resources: { clubs: [] },
    };
  },
} as AdminDataMapper;

export const clubAdminDetailsMapper = {
  map: (response: AdminDetailsResponseData<null, null, ClubAdminDetailsClubResponseData>): DetailsDataStructure => {
    const { data: responseData, resources } = response;
    const company: ClubAdminDetailsClubResponseData = responseData.club;

    return {
      admins: [
        {
          id: responseData.id.toString(),
          email: responseData.email,
          phone: responseData.phone,
          lmcStatus: responseData.is_verified,
          fcaStatus: undefined,
          fcaNumber: undefined,
          address: {
            postcode: responseData.post_code,
            city: responseData.city,
            addressLine1: responseData.street_line_1,
            addressLine2: responseData.street_line_2,
            county: responseData.county,
            houseNumber: responseData.house_number,
          },
          lastName: responseData.last_name,
          firstName: responseData.first_name,
        },
      ],
      company: {
        address: {
          houseNumber: company.house_number,
          county: company.county,
          addressLine1: company.street_line_1,
          addressLine2: company.street_line_2,
          city: company.city,
          postcode: company.post_code,
        },
        fcaNumber: undefined,
        fcaStatus: undefined,
        lmcStatus: company.is_active,
        phone: company.phone_number,
        email: company.email,
        id: company.id.toString(),
        name: company.name,
        registeredAt: '',
      },
      resources,
    };
  },
} as AdminDetailsDataMapper;

export const firmAdminDetailsMapper = {
  map: (response: AdminDetailsResponseData<FirmAdminDetailsFirmResponseData, null, null>): DetailsDataStructure => {
    const { data: responseData, resources } = response;
    const company: FirmAdminDetailsFirmResponseData = responseData.firm;

    return {
      admins: [
        {
          id: responseData.id.toString(),
          email: responseData.email,
          phone: responseData.phone,
          lmcStatus: responseData.is_verified,
          fcaStatus: responseData.fca ? responseData.fca.is_active : undefined,
          fcaNumber: responseData.fca ? responseData.fca.number : undefined,
          address: {
            postcode: responseData.post_code,
            city: responseData.city,
            addressLine1: responseData.street_line_1,
            addressLine2: responseData.street_line_2,
            county: responseData.county,
            houseNumber: responseData.house_number,
          },
          lastName: responseData.last_name,
          firstName: responseData.first_name,
        },
      ],
      company: {
        address: {
          houseNumber: company.house_number,
          county: company.county,
          addressLine1: company.street_line_1,
          addressLine2: company.street_line_2,
          city: company.city,
          postcode: company.post_code,
        },
        fcaNumber: undefined,
        fcaStatus: undefined,
        lmcStatus: company.is_active,
        phone: company.phone,
        email: company.email,
        id: company.id.toString(),
        name: company.name,
        submissionRoute: { value: company.club || '', option: getClubName(company.club, resources.clubs) },
        registeredAt: '',
      },
      resources,
    };
  },
} as AdminDetailsDataMapper;

export const networkAdminDetailsMapper = {
  map: (
    response: AdminDetailsResponseData<null, NetworkAdminDetailsNetworkResponseData, null>,
  ): DetailsDataStructure => {
    const { data: responseData, resources } = response;
    const company: NetworkAdminDetailsNetworkResponseData = responseData.network;

    return {
      admins: [
        {
          id: responseData.id.toString(),
          email: responseData.email,
          phone: responseData.phone,
          lmcStatus: responseData.is_verified,
          fcaStatus: responseData.fca ? responseData.fca.is_active : undefined,
          fcaNumber: responseData.fca ? responseData.fca.number : undefined,
          address: {
            postcode: responseData.post_code,
            city: responseData.city,
            addressLine1: responseData.street_line_1,
            addressLine2: responseData.street_line_2,
            county: responseData.county,
            houseNumber: responseData.house_number,
          },
          lastName: responseData.last_name,
          firstName: responseData.first_name,
        },
      ],
      company: {
        address: {
          houseNumber: company.house_number,
          county: company.county,
          addressLine1: company.street_line_1,
          addressLine2: company.street_line_2,
          city: company.city,
          postcode: company.post_code,
        },
        fcaNumber: undefined,
        fcaStatus: undefined,
        lmcStatus: company.is_active,
        phone: company.phone_number,
        email: company.email,
        id: company.id.toString(),
        name: company.name,
        registeredAt: '',
      },
      resources: resources,
    };
  },
} as AdminDetailsDataMapper;
